import { useMemo, useState } from "react";
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceDot, ResponsiveContainer } from "recharts";
import { fmtDate } from "@/lib/format";

// Planned / Actual / Outlook are shown by default. The three category lines are
// off until their chip is clicked.
const SERIES = [
  { key: "actual_pct", name: "Actual", color: "#2563EB", width: 3, main: true },
  { key: "planned_pct", name: "Planned", color: "#F59E0B", width: 2.25, dash: "6 5", main: true },
  { key: "outlook_pct", name: "Outlook", color: "#94A3B8", width: 2, dash: "2 5", main: true },
  { key: "engineering_pct", name: "Engineering", color: "#0EA5E9", width: 1.75 },
  { key: "procurement_pct", name: "Procurement", color: "#8B5CF6", width: 1.75 },
  { key: "construction_pct", name: "Construction", color: "#14B8A6", width: 1.75 },
];

const iso = (t) => { const d = new Date(t); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const P = (v) => `${(Number(v) * 100).toFixed(1)}%`;
const TODAY = "#F43F5E";
const AXIS = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };

function ChartTooltip({ active, payload, label, hidden }) {
  if (!active || !payload?.length) return null;
  const seen = new Set();
  const rows = payload
    .filter((p) => p.value != null && !hidden.has(p.dataKey) && !seen.has(p.dataKey) && seen.add(p.dataKey))
    .map((p) => ({ ...p, s: SERIES.find((x) => x.key === p.dataKey) }))
    .filter((p) => p.s);
  if (!rows.length) return null;
  return (
    <div className="min-w-[170px] rounded-xl border border-border bg-card/95 px-3 py-2.5 text-xs shadow-xl backdrop-blur">
      <div className="mb-1.5 font-semibold text-foreground">{fmtDate(iso(label))}</div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ background: r.s.color }} />{r.s.name}
            </span>
            <span className="font-semibold tabular-nums text-foreground">{P(r.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums leading-tight" style={tone ? { color: tone } : undefined}>{value}</div>
    </div>
  );
}

export default function SCurveChart({ curve, dashboard, asOf }) {
  const [hidden, setHidden] = useState(() => new Set(SERIES.filter((s) => !s.main).map((s) => s.key)));
  const toggle = (key) => setHidden((h) => { const n = new Set(h); n.has(key) ? n.delete(key) : n.add(key); return n; });

  const { data, liveLabel, latest } = useMemo(() => {
    const snaps = curve.filter((p) => p.planned_pct !== undefined || p.actual_pct !== undefined);
    const outlook = curve.filter((p) => p.planned_pct === undefined && p.actual_pct === undefined);
    const last = snaps[snaps.length - 1];
    let live = null;
    // The last saved snapshot can be days old; add today's live numbers so the
    // chart agrees with the cards above it.
    if (dashboard && asOf && (!last || String(last.date).slice(0, 10) < asOf)) {
      const c = dashboard.categories || {};
      live = {
        date: asOf, dateLabel: fmtDate(asOf),
        actual_pct: dashboard.overall_actual_pct, planned_pct: dashboard.overall_planned_pct,
        engineering_pct: c.ENGINEERING?.actual_pct, procurement_pct: c.PROCUREMENT?.actual_pct, construction_pct: c.CONSTRUCTION?.actual_pct,
        outlook_pct: dashboard.overall_actual_pct,
      };
    }
    const all = [...snaps, ...(live ? [live] : []), ...outlook].map((p) => ({ ...p, t: new Date(String(p.date).slice(0, 10) + "T00:00:00").getTime() }));
    const lastPt = [...all].reverse().find((p) => p.actual_pct != null);
    return { data: all, liveLabel: live ? all.find((p) => p.date === live.date)?.t : null, latest: lastPt };
  }, [curve, dashboard, asOf]);

  const few = data.filter((p) => p.actual_pct !== undefined).length <= 3;
  const variance = latest && latest.planned_pct != null ? latest.actual_pct - latest.planned_pct : null;
  const show = (k) => !hidden.has(k);

  return (
    <div data-testid="s-curve-chart" className="[&_.recharts-wrapper]:outline-none [&_.recharts-wrapper_*]:outline-none [&_svg]:outline-none">
      {latest && (
        <div className="mb-3 grid grid-cols-3 gap-2 sm:max-w-md">
          <Stat label="Actual" value={P(latest.actual_pct)} tone="#2563EB" />
          <Stat label="Planned" value={latest.planned_pct != null ? P(latest.planned_pct) : "—"} tone="#D97706" />
          <Stat label="Variance" value={variance == null ? "—" : `${variance >= 0 ? "+" : ""}${(variance * 100).toFixed(1)}%`} tone={variance == null ? undefined : variance >= 0 ? "#059669" : TODAY} />
        </div>
      )}

      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={data} margin={{ top: 16, right: 20, left: 0, bottom: 4 }} style={{ outline: "none" }} accessibilityLayer={false}>
          <defs>
            <linearGradient id="sc-actual" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2563EB" stopOpacity={0.28} />
              <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="sc-planned" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.10} />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.6} />
          <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]} tickFormatter={(t) => fmtDate(iso(t))}
            tickLine={false} axisLine={false} tick={AXIS} minTickGap={44} tickMargin={8} />
          <YAxis domain={[0, (max) => Math.max(1, max)]} ticks={[0, 0.25, 0.5, 0.75, 1]} tickLine={false} axisLine={false} width={42}
            tick={AXIS} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
          <Tooltip content={<ChartTooltip hidden={hidden} />} cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.4, strokeDasharray: "3 3" }} />
          {liveLabel != null && (
            <ReferenceLine x={liveLabel} stroke={TODAY} strokeOpacity={0.7} strokeDasharray="3 4"
              label={{ value: "Today", position: "insideTopRight", fill: TODAY, fontSize: 11, fontWeight: 600 }} />
          )}

          {show("planned_pct") && <Area type="monotone" dataKey="planned_pct" stroke="none" fill="url(#sc-planned)" connectNulls isAnimationActive={false} legendType="none" tooltipType="none" activeDot={false} />}
          {show("actual_pct") && <Area type="monotone" dataKey="actual_pct" stroke="none" fill="url(#sc-actual)" connectNulls isAnimationActive={false} legendType="none" tooltipType="none" activeDot={false} />}

          {SERIES.map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={s.width}
              strokeDasharray={s.dash} strokeLinecap="round" dot={few && s.main ? { r: 3, strokeWidth: 2, fill: "hsl(var(--card))" } : false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: "hsl(var(--card))" }} connectNulls hide={hidden.has(s.key)} isAnimationActive={false} />
          ))}

          {latest && show("actual_pct") && (
            <ReferenceDot x={latest.t} y={latest.actual_pct} r={6} fill="#2563EB" stroke="hsl(var(--card))" strokeWidth={3} ifOverflow="extendDomain" />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="s-curve-legend">
        {SERIES.map((s) => {
          const off = hidden.has(s.key);
          return (
            <button key={s.key} type="button" onClick={() => toggle(s.key)} aria-pressed={!off}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${off ? "border-border text-muted-foreground opacity-60 hover:opacity-100" : "border-transparent text-foreground"}`}
              style={off ? undefined : { background: `${s.color}1A` }}>
              <span className="h-2 w-2 rounded-full" style={{ background: off ? "hsl(var(--muted-foreground))" : s.color }} />
              {s.name}
            </button>
          );
        })}
      </div>

      {few && <p className="mt-2 text-xs text-muted-foreground">Only a few daily snapshots exist so far. The curve fills in as more are recorded (use Recompute to add today's).</p>}
    </div>
  );
}
